/* eslint-disable react-refresh/only-export-components */
import { createContext, useState, useEffect, useContext } from 'react';

interface ThemeContextType {
  theme: string;
  toggleTheme: () => void;
}

// Create a context to store theme information
const ThemeContext = createContext<ThemeContextType | null>(null);

interface ThemeProviderProps {
  children: React.ReactNode;
}

// ThemeProvider Component
export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  const [theme, setTheme] = useState(() => {
    // Check if we're in the browser
    if (typeof window !== 'undefined') {
      // Check local storage or default to 'light-theme'
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        return savedTheme;
      }
      // Check if HTML element already has a theme class
      const htmlElement = document.documentElement;
      if (htmlElement.classList.contains('dark-theme')) {
        return 'dark-theme';
      }
      if (htmlElement.classList.contains('light-theme')) {
        return 'light-theme';
      }
    }
    return 'light-theme';
  });

  // Apply the theme class to the root document element
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const root = document.documentElement;
      root.classList.remove('light-theme', 'dark-theme');
      root.classList.add(theme);
      
      // Save the theme to local storage
      localStorage.setItem('theme', theme);
    }
  }, [theme]);

  // Toggle between light and dark themes
  const toggleTheme = () => {
    setTheme((prevTheme) => (prevTheme === 'light-theme' ? 'dark-theme' : 'light-theme'));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
